/** DataBest — Dashboard filters bar. */
import React, { useEffect, useState } from "react";
import { individualsApi } from "@/lib/planApi";
import DatePickerUS from "@/components/DatePickerUS";

const PRESETS = [
    { key: "1d",  label: "1 day",  days: 1 },
    { key: "7d",  label: "7d",  days: 7 },
    { key: "30d", label: "30d", days: 30 },
    { key: "90d", label: "90d", days: 90 },
];

const GRANULARITIES = [
    { key: "day",   label: "Day" },
    { key: "week",  label: "Week" },
    { key: "month", label: "Month" },
];

const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString();
};

const startOfDayISO = (yyyyMmDd) =>
    new Date(`${yyyyMmDd}T00:00:00Z`).toISOString();
const endOfDayISO = (yyyyMmDd) =>
    new Date(`${yyyyMmDd}T23:59:59Z`).toISOString();

const isoToDate = (iso) => (iso ? iso.slice(0, 10) : "");

const DashboardFilters = ({ filters, onChange }) => {
    const [individuals, setIndividuals] = useState([]);
    const [individualsLoading, setIndividualsLoading] = useState(true);

    useEffect(() => {
        individualsApi
            .list({ active: true })
            .then((r) => setIndividuals(r || []))
            .catch(() => setIndividuals([]))
            .finally(() => setIndividualsLoading(false));
    }, []);

    const setPreset = (days, key) => {
        onChange({
            from: isoDaysAgo(days),
            to: new Date().toISOString(),
            preset: key,
        });
    };

    const setCustom = () => {
        // Initialise custom range with the current window if unset
        const from = filters.from || isoDaysAgo(7);
        const to = filters.to || new Date().toISOString();
        onChange({ from, to, preset: "custom" });
    };

    const isCustom = filters.preset === "custom";

    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4"
            data-testid="dashboard-filters"
        >
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-1.5">
                    {PRESETS.map((p) => {
                        const active = filters.preset === p.key;
                        return (
                            <button
                                key={p.key}
                                onClick={() => setPreset(p.days, p.key)}
                                data-testid={`preset-${p.key}`}
                                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                    active
                                        ? "bg-brand-blue-700 text-white"
                                        : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                }`}
                            >
                                {p.label === "1 day" ? "Today" : `Last ${p.label}`}
                            </button>
                        );
                    })}
                    <button
                        onClick={setCustom}
                        data-testid="preset-custom"
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                            isCustom
                                ? "bg-brand-blue-700 text-white"
                                : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                    >
                        Custom
                    </button>
                </div>

                {isCustom && (
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            From
                        </label>
                        <DatePickerUS
                            value={isoToDate(filters.from)}
                            onChange={(iso) => {
                                if (!iso) return;
                                onChange({ from: startOfDayISO(iso) });
                            }}
                            testId="dashboard-from"
                        />
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            To
                        </label>
                        <DatePickerUS
                            value={isoToDate(filters.to)}
                            onChange={(iso) => {
                                if (!iso) return;
                                onChange({ to: endOfDayISO(iso) });
                            }}
                            testId="dashboard-to"
                        />
                    </div>
                )}

                <div className="h-6 w-px bg-slate-200" />

                <div className="flex items-center gap-2">
                    <label
                        htmlFor="filter-individual"
                        className="text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                    >
                        Individual
                    </label>
                    <select
                        id="filter-individual"
                        data-testid="filter-individual"
                        value={filters.individual_id || ""}
                        onChange={(e) =>
                            onChange({ individual_id: e.target.value || null })
                        }
                        disabled={individualsLoading}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 disabled:opacity-60"
                    >
                        <option value="">
                            {individualsLoading ? "Loading…" : "All"}
                        </option>
                        {individuals.map((i) => (
                            <option key={i.id} value={i.id}>
                                {`${i.first_name} ${i.last_name}`}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="h-6 w-px bg-slate-200" />

                <div className="flex items-center gap-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Granularity
                    </label>
                    <div className="flex gap-1">
                        {GRANULARITIES.map((g) => {
                            const active = filters.granularity === g.key;
                            return (
                                <button
                                    key={g.key}
                                    onClick={() =>
                                        onChange({ granularity: g.key })
                                    }
                                    data-testid={`granularity-${g.key}`}
                                    className={`rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                        active
                                            ? "bg-slate-800 text-white"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardFilters;
